/**
 * API client for the Nexus Worker.
 *
 * All paths are relative to current origin; in dev mode Vite proxies
 * /api/* to nexus.techflouu.com (see vite.config.ts).
 */

const API = "/api";


/* ---------- types (mirror the Worker's response shapes) ---------- */

export interface Session {
  id: string;
  instance_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: number;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  tool_calls: Array<{ name: string; input: unknown; result_summary: string; ok?: boolean }>;
  citations: string[];
  tokens_in: number;
  tokens_out: number;
  created_at: string;
  /** Files sent with a user message (shown as chips in the bubble). */
  attachments?: { name: string }[];
}

export interface SynapsePage {
  id: string;
  header: Record<string, unknown>;
  body: string;
  path: string;
}

export interface HealthResponse {
  ok: boolean;
  worker_version: string;
  bindings: {
    r2_corpus: boolean;
    kv: boolean;
    anthropic_key_present: boolean;
  };
  synapse_bundle: { total_pages: number; instances: number };
  available_instances: string[];
  phase: string;
}


/* ---------- low-level fetch wrapper ---------- */

async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const r = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`API ${r.status} ${path}: ${text.slice(0, 300)}`);
  }
  return r.json() as Promise<T>;
}


/* ---------- typed endpoints ---------- */

export function getHealth(): Promise<HealthResponse> {
  return api("/health");
}

export function listSessions(
  instance: string,
): Promise<{ sessions: Session[] }> {
  return api(`/${instance}/sessions`);
}

export function createSession(
  instance: string,
  title?: string,
): Promise<{ session: Session }> {
  return api(`/${instance}/sessions`, {
    method: "POST",
    body: JSON.stringify({ title: title || "New chat" }),
  });
}

export function getSession(
  instance: string,
  sid: string,
): Promise<{ session: Session; messages: Message[] }> {
  return api(`/${instance}/sessions/${sid}`);
}

export function renameSession(
  instance: string,
  sid: string,
  title: string,
): Promise<{ ok: boolean }> {
  return api(`/${instance}/sessions/${sid}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
}

export function deleteSession(
  instance: string,
  sid: string,
): Promise<{ ok: boolean }> {
  return api(`/${instance}/sessions/${sid}`, { method: "DELETE" });
}

export function fetchPage(
  instance: string,
  pageId: string,
): Promise<SynapsePage> {
  return api(`/${instance}/fetch_by_id?id=${encodeURIComponent(pageId)}`);
}

/** Resolve an `entity:<type>/<slug>` ref to its synapse page, or null when the
 *  entity has no dedicated page (a reference-only tag). */
export async function resolveEntity(
  instance: string,
  entityRef: string,
): Promise<SynapsePage | null> {
  try {
    return await api(`/${instance}/resolve_entity?entity_ref=${encodeURIComponent(entityRef)}`);
  } catch {
    return null;   // 404 → no entity page; chip stays a plain tag
  }
}

/** Returns the URL for the SSE ask endpoint — POST + body=question. */
export function askUrl(instance: string, sid: string): string {
  return `${API}/${instance}/sessions/${sid}/ask`;
}

/** URL for a raw corpus blob (R2-backed, served by the Worker). */
export function blobUrl(instance: string, corpusPath: string): string {
  return `${API}/${instance}/blob?path=${encodeURIComponent(corpusPath)}`;
}

/** Opaque, stable corpus ref = sha256("<instance>/<uri>")[:32]. Matches the
 * Worker's resolver so the raw path never appears in the URL (no enumeration). */
export async function corpusHash(instance: string, uri: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${instance}/${uri}`));
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
}

/** Secure corpus download URL — the Worker RBAC-checks then streams from R2. */
export function corpusUrl(instance: string, hash: string): string {
  return `${API}/${instance}/corpus/${hash}`;
}

/** Resolve a source `uri` to its link: external URL as-is; otherwise the opaque
 * RBAC-gated corpus URL. Used to render synapse-page source hyperlinks. */
export async function sourceHref(instance: string, uri: string): Promise<string> {
  if (/^https?:\/\//i.test(uri)) return uri;
  return corpusUrl(instance, await corpusHash(instance, uri));
}

/** A page's raw source, resolved by the Worker: R2 corpus link (prose),
 *  GitHub file view (code-archetype), or the external URL as-is. */
export interface SourceLink {
  uri: string;
  kind: "github" | "r2" | "external";
  url: string;
  label: string;
}
export interface SourceLinksResponse {
  instance: string;
  page_id: string;
  archetype: "prose" | "code";
  sources: SourceLink[];
}
export function getSourceLinks(instance: string, pageId: string): Promise<SourceLinksResponse> {
  return api(`/${instance}/source-link?page=${encodeURIComponent(pageId)}`);
}


/* ---------- HR directory (letter-form auto-population) ---------- */

/** One synced employee row served by the whitelabel front door's /api/hr
 *  route (session + plan checked there; the HR-system bearer never reaches
 *  the browser). */
export interface HrEmployee {
  slug: string;
  display_name: string;
  email: string;
  employment_type: string | null;
  job_title: string | null;
  current_salary_sgd: number | null;
  source: string | null;
  synced_at: string | null;
}

export interface HrCompany {
  company_id: string;
  org_name: string | null;
  source: string | null;
  synced_at: string | null;
}

export interface HrDirectory {
  company: HrCompany | null;
  employees: HrEmployee[];
}

/** Fetch the tenant's synced HR directory for form prefill. Null when the
 *  host serves no /api/hr route (non-whitelabel origins) or the tenant's
 *  plan is below the auto-population floor — the form then runs on manual
 *  entry. */
export async function getHrDirectory(): Promise<HrDirectory | null> {
  try {
    const [empRes, coRes] = await Promise.all([
      fetch(`${API}/hr/employees`),
      fetch(`${API}/hr/company`),
    ]);
    if (!empRes.ok) return null;
    const emp = await empRes.json() as { employees?: HrEmployee[] };
    if (!Array.isArray(emp.employees)) return null;
    const company = coRes.ok ? await coRes.json() as HrCompany : null;
    return { company, employees: emp.employees };
  } catch {
    return null;
  }
}


/* ---------- admin / instance creation (P16.3) ---------- */

export interface CreateInstanceResponse {
  ok: boolean;
  slug: string;
  branch: string;
  pr_number: number;
  pr_url: string;
  bearer_token: string;          // P16.5 — shown once
  bearer_token_warning: string;
  message: string;
}

export function createInstance(opts: {
  slug: string;
  description: string;
  domain_shape: "informational" | "workflow";
  archetype: "prose" | "code";
  owner_email: string;
  owner_password: string;
}): Promise<CreateInstanceResponse> {
  return api("/admin/instances/create", {
    method: "POST",
    body: JSON.stringify(opts),
  });
}


/* ---------- members (H — owner-managed) ---------- */

export type MemberRole = "owner" | "admin" | "writer" | "member";
/** The roles an owner can grant. */
export type GrantableRole = "member" | "writer" | "admin";

export interface Member {
  email: string;
  instance_id: string;
  role: MemberRole;
  invited_by: string | null;
  created_at: string;
}

export function listMembers(instance: string): Promise<{ instance: string; you: MemberRole | null; members: Member[] }> {
  // DEV-only sample roster for the ?devauth preview (stripped from prod builds).
  if (import.meta.env.DEV && new URLSearchParams(location.search).has("devauth")) {
    return Promise.resolve({
      instance, you: "owner" as MemberRole, members: [
        { email: "owner@local", instance_id: instance, role: "owner", invited_by: null, created_at: "2026-06-07" },
        { email: "writer@local", instance_id: instance, role: "writer", invited_by: "owner@local", created_at: "2026-06-07" },
        { email: "member@local", instance_id: instance, role: "member", invited_by: "owner@local", created_at: "2026-06-07" },
      ],
    });
  }
  return api(`/${instance}/members`);
}

/** Invite a member. Omit `password` (the default) to email them a sign-in link
 *  they use to set their own; pass one to pre-set it. */
export function inviteMember(instance: string, email: string, role: GrantableRole, password?: string): Promise<{ ok: boolean; email: string; role: string; invited: boolean }> {
  const body: Record<string, unknown> = { email, role };
  if (password) body.password = password;
  return api(`/${instance}/members/invite`, { method: "POST", body: JSON.stringify(body) });
}

export function elevateMember(instance: string, email: string, role: GrantableRole): Promise<{ ok: boolean; email: string; role: string }> {
  return api(`/${instance}/members/elevate`, { method: "POST", body: JSON.stringify({ email, role }) });
}

export function removeMember(instance: string, email: string): Promise<{ ok: boolean; removed: string }> {
  return api(`/${instance}/members/remove`, { method: "POST", body: JSON.stringify({ email }) });
}


/* ---------- API clients (admin-managed) ---------- */

export type ClientScope = "mcp:read" | "mcp:tools";

export interface ApiClient {
  client_id: string;
  client_name: string;
  scope: string;
  created_at: string;
  created_by: string | null;
  revoked_at: string | null;
}

/** The create and rotate responses: the client plus its secret, shown once. */
export interface ApiClientSecret extends ApiClient {
  client_secret: string;
  mcp_endpoint: string;
  retrieve_endpoint: string;
}

export function listApiClients(instance: string): Promise<{ instance: string; clients: ApiClient[] }> {
  return api(`/${instance}/admin/clients`);
}

export function createApiClient(instance: string, client_name: string, scope: string): Promise<ApiClientSecret> {
  return api(`/${instance}/admin/clients`, { method: "POST", body: JSON.stringify({ client_name, scope }) });
}

export function rotateApiClientSecret(instance: string, clientId: string): Promise<ApiClientSecret> {
  return api(`/${instance}/admin/clients/${clientId}/rotate-secret`, { method: "POST" });
}

export function revokeApiClient(instance: string, clientId: string): Promise<ApiClient> {
  return api(`/${instance}/admin/clients/${clientId}`, { method: "DELETE" });
}


export interface InstanceStatus {
  slug: string;
  branch?: string | null;
  state:
    | "ready"
    | "in_progress"
    | "merged"
    | "deploying"
    | "propagating"
    | "ingest_failed"
    | "deploy_failed"
    | "failed"
    | "unknown";
  url?: string;
  bundle_generated_at?: string;
  init?: {
    name: string;
    status: string;
    conclusion: string | null;
    url: string;
    branch: string;
    created_at: string;
  } | null;
  deploy?: {
    name: string;
    status: string;
    conclusion: string | null;
    url: string;
    branch: string;
    created_at: string;
  } | null;
  note?: string;
  // legacy back-compat (older responses)
  run?: { name: string; status: string; conclusion: string | null; url: string; branch: string };
  recent_deploy?: { status: string; conclusion: string | null; url: string } | null;
}

export function getInstanceStatus(slug: string, branch?: string): Promise<InstanceStatus> {
  const qs = branch ? `?branch=${encodeURIComponent(branch)}` : "";
  return api(`/admin/instances/${slug}/status${qs}`);
}


/* ---------- ingestion (P16.4) ---------- */

export interface UploadedFile {
  path: string;
  sha256: string;
  size: number;
  content_type: string;
}

export async function uploadBlob(
  instance: string,
  filename: string,
  file: File,
): Promise<UploadedFile> {
  const r = await fetch(
    `${API}/${instance}/ingest/blob?path=${encodeURIComponent(filename)}`,
    {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    },
  );
  if (!r.ok) throw new Error(`upload ${r.status}: ${(await r.text()).slice(0, 200)}`);
  const d = await r.json() as { sha256: string; size: number; content_type: string };
  return { path: filename, ...d };
}

export interface IngestStartResponse {
  ok: boolean;
  instance: string;
  files_count: number;
  lane: "hosted";
  /** `unknown` means the run outlasted the request that started it — it is
   *  still going, and is not repeated. */
  verdict?: "ok" | "failed" | "unknown";
  written?: number;
}

export function startIngest(
  instance: string, files: UploadedFile[], context?: string,
  githubUrl?: string, subtree?: string,
): Promise<IngestStartResponse> {
  return api(`/${instance}/ingest/start`, {
    method: "POST",
    body: JSON.stringify({
      files,
      ...(context ? { context } : {}),
      ...(githubUrl ? { github_url: githubUrl, subtree: subtree || "." } : {}),
    }),
  });
}

export interface CodeSource {
  repo: string;
  branch: string;
  subtree: string;
  profile: string;
  topics: string[];
  watch_count: number;
  page_count: number;
  webhook: "armed" | "off" | "unknown";
}

export interface SourcesResponse {
  instance: string;
  archetype: "prose" | "code";
  topics: string[];
  sources: CodeSource[];
}

// `withStatus` pulls live webhook + open-PR state (per-repo GitHub calls).
// Omit it for the cheap archetype/config read used to gate the Sources button.
export function listSources(instance: string, withStatus = false): Promise<SourcesResponse> {
  return api(`/${instance}/sources${withStatus ? "?status=1" : ""}`);
}

export function addSource(
  instance: string, repo: string, subtree: string, profile: string, topics: string[],
): Promise<{ ok: boolean; pr_number: number; pr_url: string }> {
  return api(`/${instance}/sources/add`, {
    method: "POST",
    body: JSON.stringify({ repo, subtree, profile, topics }),
  });
}

export function rederiveSource(
  instance: string, repo: string,
): Promise<{ ok: boolean; pr_number: number; pr_url: string }> {
  return api(`/${instance}/sources/rederive`, {
    method: "POST",
    body: JSON.stringify({ repo }),
  });
}

export function reingestSource(
  instance: string, repo: string,
): Promise<{
  ok: boolean; lane?: "hosted" | "committed"; verdict?: "dispatched";
  files?: number; base?: string | null; head?: string;
  pr_number?: number; pr_url?: string;
}> {
  return api(`/${instance}/sources/reingest`, {
    method: "POST",
    body: JSON.stringify({ repo }),
  });
}

export function armWebhook(
  instance: string, repo: string,
): Promise<{ ok: boolean; armed?: boolean; already_armed?: boolean }> {
  return api(`/${instance}/sources/webhook`, {
    method: "POST",
    body: JSON.stringify({ repo }),
  });
}


/* ---------- review gate (Phase A / gap 1) ---------- */

export interface ChangeSet {
  pr_number: number;
  title: string;
  branch: string;
  url: string;
  created_at: string;
  needs_review: boolean;
}

export interface ChangeDiffFile {
  page: string;          // synapse page id (path minus the synapse prefix + .md)
  filename: string;
  status: string;        // added | modified | removed
  additions: number;
  deletions: number;
  patch: string | null;  // unified diff
}

export interface ChangeDiff {
  instance: string;
  pr_number: number;
  branch: string;
  merged: boolean;
  mergeable: boolean | null;
  mergeable_state: string;
  files: ChangeDiffFile[];
}

export function listChanges(instance: string): Promise<{ instance: string; changes: ChangeSet[] }> {
  return api(`/${instance}/changes`);
}

export function getChangeDiff(instance: string, pr: number): Promise<ChangeDiff> {
  return api(`/${instance}/changes/${pr}/diff`);
}

export function approveChange(
  instance: string, pr: number,
): Promise<{ ok: boolean; merged?: boolean; already_merged?: boolean }> {
  return api(`/${instance}/changes/${pr}/approve`, { method: "POST" });
}

export function rejectChange(
  instance: string, pr: number,
): Promise<{ ok: boolean; rejected?: boolean }> {
  return api(`/${instance}/changes/${pr}/reject`, { method: "POST" });
}


/* ---------- skill authoring (Phase C / gap 4) ---------- */

export interface SkillDescriptor {
  id: string;
  description: string;
  input_schema: unknown;
  output_schema?: unknown;
  version?: string;
}

export interface SkillListResponse {
  instance: string;
  executor: string;            // "http" | "stub"
  executor_ready: boolean;
  skills: SkillDescriptor[];
}

export function listSkills(instance: string): Promise<SkillListResponse> {
  return api(`/${instance}/skills`);
}

/** Instance-declared Felix pipeline graphs (committed under instances/<inst>/pipelines/).
 *  `drive_enabled` is true when the instance has a Drive input connector configured. */
export function listPipelines(instance: string): Promise<{ instance: string; pipelines: unknown[]; drive_enabled?: boolean }> {
  return api(`/${instance}/pipelines`);
}


/* ---------- Google Drive input connector (Phase 3 — client-folder picker) ---------- */

export interface DriveFolder { id: string; name: string; }
export interface DriveFile {
  id: string; name: string; path: string; size: number; modifiedTime?: string;
}
export interface DriveListing {
  files: DriveFile[];
  skipped_folders: string[];
  skipped_files: { name: string; path: string; reason: string }[];
  truncated: boolean;
}
export interface DriveStaged {
  batch: string;
  staged: { name: string; key: string; size: number; drive_file_id: string }[];
  skipped: { id: string; name?: string; reason: string }[];
}

/** Search the connected Drive for a client's folder by name. */
export function driveFindFolders(instance: string, q: string): Promise<{ ok: boolean; folders: DriveFolder[] }> {
  return api(`/${instance}/drive/folders?q=${encodeURIComponent(q)}`);
}

/** List a client folder's pipeline-eligible files (recursive, rule-filtered). */
export function driveListFiles(instance: string, folderId: string): Promise<DriveListing> {
  return api(`/${instance}/drive/files?folder=${encodeURIComponent(folderId)}`);
}

/** Stage the chosen Drive files into R2; returns the input-node keys. */
export function driveStage(instance: string, fileIds: string[]): Promise<DriveStaged> {
  return api(`/${instance}/drive/stage`, { method: "POST", body: JSON.stringify({ file_ids: fileIds }) });
}

export interface SkillDetail {
  instance: string;
  id: string;
  path: string;
  yaml: string;
}

export function getSkill(instance: string, id: string): Promise<SkillDetail> {
  return api(`/${instance}/skills/${encodeURIComponent(id)}`);
}

export interface SkillUpsertResponse {
  ok: boolean;
  instance: string;
  id: string;
  pr_number: number;
  pr_url: string;
  branch: string;
}

export function upsertSkill(
  instance: string, id: string, yaml: string, message?: string, pipeline?: unknown,
): Promise<SkillUpsertResponse> {
  return api(`/${instance}/skills/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify({ yaml, message, pipeline }),
  });
}

export interface SkillTestResponse {
  ok: boolean;
  instance?: string;
  id?: string;
  payload?: unknown;
  tool_calls?: Array<{ name: string; input: unknown; result_summary?: string }>;
  cited_synapses?: string[];
  warnings?: string[];
  attachments?: Array<{ name: string; mime: string; bytes: number }>;
  error?: string;
  hint?: string;
}

/** Test-run a skill via the executor. Tolerant: returns the structured body
 *  even on non-2xx (executor errors carry {ok:false,error,hint}). */
export async function testSkill(
  instance: string, id: string, input: Record<string, unknown>,
): Promise<SkillTestResponse> {
  const r = await fetch(`${API}/${instance}/skills/${encodeURIComponent(id)}/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input }),
  });
  const body = await r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
  return body as SkillTestResponse;
}
